/**
 * File: SectionMenuPage.tsx
 * Path: apps/admin/src/routes/menu/SectionMenuPage.tsx
 * Description: A section's own page, opened from the phone bottom bar
 * (CRM, Accounting, CMS): a line on what needs attention, then one large
 * row per page in the section. Built from SIDEBAR_ITEMS, so a page added to
 * a group appears here by itself, and roles see only what they can open.
 */

import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ADMIN_PATHS } from "@idk/config";
import { ChevronRight } from "lucide-react";
import { PageHeader } from "@/modules/layout/components/PageHeader";
import { navIcon } from "@/modules/layout/utils/navIconsUtils";
import { useSectionMenuPageController } from "@/features/menu/hooks/useSectionMenuPageController";
import { SectionGlance } from "./SectionGlance";

export const SectionMenuPage: React.FC = () => {
  const { group, canSeeLeads } = useSectionMenuPageController();

  if (!group || !group.children) return <Navigate to={ADMIN_PATHS.dashboard} replace />;

  return (
    <div className="space-y-stack-xl">
      <PageHeader title={group.label} />
      <SectionGlance hub={group.hub!} canSeeLeads={canSeeLeads} />
      <nav aria-label={group.label}>
        <ul className="flex flex-col gap-stack-xs">
          {group.children.map((child) => {
            if (!child.href) return null;
            const Icon = navIcon(child.icon);
            return (
              <li key={child.href}>
                <Link
                  to={child.href}
                  className="flex min-h-16 items-center gap-stack-md border border-border/60 bg-card p-gutter-sm transition-colors hover:border-primary/40 hover:bg-foreground/5"
                >
                  <Icon className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold uppercase tracking-wider">
                      {child.label}
                    </span>
                    {child.description && (
                      <span className="block text-sm text-muted-foreground">
                        {child.description}
                      </span>
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};
